/**
 * AtlasDroidModule — the crafted droid made VISIBLE: when the player owns a
 * droid_ item (crafted at a workshop or salvaged), a small hover droid
 * follows them around the Commons — spring-chased, bobbing, glowing by
 * model (Aegis blue combat sphere · Ember amber work tracker).
 *
 * Client-side set dressing over the replicated player_item rows (the droid
 * ITEM is the authority; this renders whichever droid the player owns).
 * In space the same item docks at the ship's droid port instead
 * (SpaceModule's stats.droid — repair ticks), so the follower hides there.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { makeSystem, Phase } from '../engine-compat.js';

const FOLLOW_OFFSET = new THREE.Vector3(1.6, 1.7, -0.6); // right shoulder, above
const SPRING = 4.5;         // /s chase stiffness
const POLL_S = 2;

export const DROID_LOOK: Record<string, { color: number; emissive: number }> = {
  droid_aegis: { color: 0x35507a, emissive: 0x4f8cf0 },
  droid_ember: { color: 0x6a4a2c, emissive: 0xff9a3c },
  // THE DROID CLASSES (2026-09-24): the primitive stand-in's colours while a class model streams.
  droid_battle: { color: 0x4a4f58, emissive: 0x3fd0c8 },
  droid_supply: { color: 0x5b5a45, emissive: 0xffb347 },
  droid_medic: { color: 0xd8dde0, emissive: 0x2fe0c0 },
  droid_humanoid: { color: 0xc9c9c4, emissive: 0xffb347 },
  droid_ship: { color: 0x5a6f86, emissive: 0x3fd0c8 },
  droid_mega: { color: 0x6b6150, emissive: 0xffa028 },
};

/** Only the two older droids ride your shoulder as a follower orb while in the bag. */
const FOLLOWER_KINDS = new Set(['droid_aegis', 'droid_ember']);

/** The shared droid body (follower + deployed units render the same machine). */
export function buildDroidMesh(kind: string): THREE.Group {
  const look = DROID_LOOK[kind] ?? DROID_LOOK.droid_aegis;
  const g = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.22, 1),
    new THREE.MeshStandardMaterial({ color: look.color, emissive: look.emissive, emissiveIntensity: 0.85, roughness: 0.4, metalness: 0.5 }),
  );
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(0.3, 0.035, 8, 24),
    new THREE.MeshStandardMaterial({ color: 0x22303f, emissive: look.emissive, emissiveIntensity: 0.4, roughness: 0.6 }),
  );
  ring.rotation.x = Math.PI / 2;
  g.add(body, ring);
  return g;
}

export class AtlasDroidModule implements IModule {
  readonly name = 'AtlasDroid';

  private app: App | null = null;
  private droid: THREE.Group | null = null;
  private kind = '';
  private pollAcc = 0;
  private bob = 0;

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('DroidFollower', Phase.Animation, 39, (dt) => this.update(dt)));
  }

  /** The droid the player owns right now ('' = none). Space hides it. */
  private ownedDroid(): string {
    if (atlas.spaceFlight) return '';
    const conn = atlas.conn;
    if (!conn?.identity) return '';
    try {
      for (const it of conn.db.playerItem.iter()) {
        if (it.owner.isEqual(conn.identity) && FOLLOWER_KINDS.has(it.itemKey)) {
          return it.itemKey;
        }
      }
    } catch { /* row cache mid-churn */ }
    return '';
  }

  private build(kind: string): THREE.Group {
    return buildDroidMesh(kind);
  }

  private update(dt: number): void {
    this.pollAcc += dt;
    if (this.pollAcc >= POLL_S) {
      this.pollAcc = 0;
      const want = this.ownedDroid();
      if (want !== this.kind) {
        this.remove();
        this.kind = want;
        if (want && this.app) {
          this.droid = this.build(want);
          this.app.world.scene.add(this.droid);
          const rig = atlas.localMesh;
          if (rig) this.droid.position.copy(rig.position).add(FOLLOW_OFFSET);
        }
      }
    }
    const rig = atlas.localMesh;
    if (!this.droid || !rig) return;
    this.bob += dt;
    const target = this._t.copy(FOLLOW_OFFSET).applyQuaternion(rig.quaternion).add(rig.position);
    target.y += Math.sin(this.bob * 2.1) * 0.08;
    this.droid.position.lerp(target, Math.min(1, SPRING * dt));
    this.droid.rotation.y += dt * 0.9;
  }

  private _t = new THREE.Vector3();

  private remove(): void {
    if (this.droid) {
      this.app?.world.scene.remove(this.droid);
      this.droid.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
      });
      this.droid = null;
    }
  }

  dispose(): void {
    this.remove();
  }
}
